import React from "react";
import SelectWithSearch from "components/common/atoms/SelectWithSearch";
import { t } from "components/CentralDataMangement/translation/Translation";
import PCAccordion from "./PCAccordion";
import { useFormContext } from "components/CompanyCreation/AdminFiche/context/Context";
import {
  Option,
  ValidationRules,
} from "components/common/utlis/TypeAnnotations";
import * as CONST from "../../../annotations/AdminFicheConstants";
import { formatTime } from "components/common/utlis/PlanningUtility";
import {
  scrollToTop,
  validateForm,
  validateRadioField,
  validateRequired,
  validateSelectField,
} from "components/common/services/ValidationService";
import Navigation from "../../form-navigation/Navigation";

const WagePemiums = () => {
  const { state, dispatch } = useFormContext();
  const wagePremiums = Object.values(state.wageAndPremiums ?? {});

  const addWageData = (
    selectedOption: Option,
    field: string,
    wageId?: number | null,
    profileId?: number | null
  ) => {
    if (selectedOption?.value !== null) {
      dispatch({
        type: CONST.ADD_WAGE_DATA,
        field,
        value: selectedOption,
        wageId,
        profileId,
      });
    }
  };

  const validation = (
    name: string,
    value: string | number | null | Option | object[] | Date | boolean,
    wageId: number | null | undefined,
    profileId: number | null | undefined,
    isSingleFieldValidation: boolean = false
  ) => {
    const validationRules: ValidationRules = {
      isEcoChequeCp: [validateRadioField],
      valueCp: name === "chequeTypeCp" ? [] : [validateRequired],
      chequeTypeCp: [validateSelectField],
      otherInfoCp:
        name === "chequeTypeCp" && ((value as Option)?.value === 5) ? [validateRequired] : [],
    };
    const profile =
      state.wageAndPremiums?.[wageId || 0]?.commProfile?.[profileId || 0] ?? {};

    const validationErrors = validateForm(
      { ...profile, [name]: value },
      validationRules,
      isSingleFieldValidation ? name : undefined
    );

    dispatch({
      type: CONST.UPDATE_WAGE_FIELD_DATA,
      field: name + "Error",
      value: validationErrors[name],
      wageId: wageId,
      profileId: profileId,
      codeId: undefined,
      applicabilityId: undefined,
    });

    const isFieldValid = Object.keys(validationErrors).length === 0;

    if (isFieldValid) {
      dispatch({
        type: CONST.UPDATE_WAGE_FIELD_DATA,
        field: name + "Error",
        value: "",
        wageId: wageId,
        profileId: profileId,
        codeId: undefined,
        applicabilityId: undefined,
      });
    }

    if (Object.keys(validationErrors).length > 0) {
      return false;
    }
    return true;
  };

  const clonePayProfile = (
    selectedOption: Option | null,
    wageId: number,
    profileId: number
  ) => {
    if (selectedOption?.value !== null) {
      dispatch({
        type: CONST.CLONE_PAY_PROFILE,
        value: selectedOption,
        wageId,
        profileId,
      });
    }
  };

  const removeWageData = (
    wageId?: number | null,
    profileId?: number | null,
    codeId?: number | null
  ) => {
    dispatch({
      type: CONST.REMOVE_WAGE_DATA,
      wageId,
      profileId,
      codeId,
    });
  };

  const pcOptions = [
    ...state.general.paritairBlue,
    ...state.general.paritairWhite,
  ];

  const getPcDropdown = () => {
    if (!wagePremiums) return [];
    const selectedPcs = wagePremiums.map((item: any) => item.pc);
    const availableOptions = pcOptions.filter(
      (option) => !selectedPcs.some((pc: any) => pc.value === option.value)
    );
    return availableOptions.length ? availableOptions : [];
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    wageId: number,
    profileId?: number,
    codeId?: number,
    applicabilityId?: number,
    fieldName?: string
  ) => {
    let { name, value, type, checked } = e.target as HTMLInputElement;
    let updatedValue: any;

    const numericFields = [
      "totalValue",
      "employerPart",
      "minimumHours",
      "value",
      "mtcTotal",
      "mtcEmployer",
      "mtcMinHrs",
      "amount",
      "valueCp",
    ];
    // If the field is a numeric one, replace dot with comma except for info field
    if (name !== "info" && name !== "otherInfoCp") {
      value = value.replace(/\./g, ","); // Replace all '.' with ','
    }

    let decimalPattern = /^(\d{1,3})(,\d{0,4})?$/;
    if (name === "value" || name === "valueCp") {
      decimalPattern = /^(\d{1,3})(,\d{0,3})?$/;
    }

    // Format value based on field type
    if (name === "from" || name === "to") {
      updatedValue = formatTime(value);
    } else if (numericFields.includes(name)) {
      updatedValue =
        value === "" || decimalPattern.test(value) ? value : undefined;
      if (updatedValue === undefined) return;
    } else if (["percentage", "minHrs", "maxHrs"].includes(name)) {
      updatedValue =
        value === "" || !/[a-zA-Z]/.test(value) ? value : undefined;
      if (updatedValue === undefined) return;
    } else {
      updatedValue =
        type === "radio"
          ? parseInt(value)
          : type === "checkbox"
          ? checked
            ? 1
            : 0
          : value;
    }

    //Special handling for EcoCheque
    if (fieldName && fieldName === "isEcoCheque") {
      //updatevalue === 1 =>  chequeType = null
      //updatevalue === 0 => value = '' and chequeType = null
      //updatevalue === 2 => value = ''
      const updates: { field: string; value: any }[] = [];

      if (updatedValue === 0) {
        updates.push({ field: "value", value: "" });
        updates.push({ field: "chequeType", value: null });
      } else if (updatedValue === 1) {
        updates.push({ field: "chequeType", value: null });
      } else if (updatedValue === 2) {
        updates.push({ field: "value", value: "" });
      }

      updates.forEach(({ field, value }) => {
        dispatch({
          type: CONST.UPDATE_WAGE_FIELD_DATA,
          field,
          value,
          wageId,
          profileId,
          codeId,
          applicabilityId,
        });
      });
      if (updatedValue === 1 || updatedValue === 0) {
        dispatch({
          type: CONST.UPDATE_WAGE_FIELD_DATA,
          field: "otherInfo",
          value: "",
          wageId,
          profileId,
          codeId,
          applicabilityId,
        });
      }
    }

    const dispatchField = fieldName ?? name;
    // Special handling for EcoCheque
    if (fieldName && fieldName === "isEcoChequeCp") {
      //updatevalue === 1 =>  chequeTypeCp = null
      //updatevalue === 0 => valueCp = '' and chequeTypeCp = null
      //updatevalue === 2 => valueCp = ''
      const updates: { field: string; value: any }[] = [];

      if (updatedValue === 0) {
        updates.push({ field: "valueCp", value: "" });
        updates.push({ field: "chequeTypeCp", value: null });
      } else if (updatedValue === 1) {
        updates.push({ field: "chequeTypeCp", value: null });
      } else if (updatedValue === 2) {
        updates.push({ field: "valueCp", value: "" });
      }

      updates.forEach(({ field, value }) => {
        dispatch({
          type: CONST.UPDATE_WAGE_FIELD_DATA,
          field,
          value,
          wageId,
          profileId,
          codeId,
          applicabilityId,
        });
      });
      // dispatch({
      //   type: CONST.UPDATE_WAGE_FIELD_DATA,
      //   field:
      //     updatedValue === 1 || updatedValue === 0 ? "chequeTypeCp" : "valueCp",
      //   value: updatedValue === 1 || updatedValue === 0 ? null : "",
      //   wageId,
      //   profileId,
      //   codeId,
      //   applicabilityId,
      // });
      if (updatedValue === 1 || updatedValue === 0) {
        dispatch({
          type: CONST.UPDATE_WAGE_FIELD_DATA,
          field: "otherInfoCp",
          value: "",
          wageId,
          profileId,
          codeId,
          applicabilityId,
        });
      }
    }

    // --- Main dispatch ---
    const mealVoucherFields = [
      "totalValue",
      "employerPart",
      "minimumHours",
      "value",
      "otherInfo",
    ];

    dispatch({
      type:
        mealVoucherFields.includes(name) || dispatchField === "isEcoCheque"
          ? CONST.UPDATE_MEAL_VOUCHERS
          : CONST.UPDATE_WAGE_FIELD_DATA,
      field: dispatchField,
      value: updatedValue,
      wageId,
      profileId,
      codeId,
      applicabilityId,
    });
    const validationFields = ["isEcoChequeCp", "valueCp", "otherInfoCp"];
    if (validationFields.includes(name)) {
      validation(name, updatedValue, wageId, profileId);
    }
  };

  const handleDateChange = (
    date: Date | null,
    name: string,
    wageId: number,
    profileId?: number,
    codeId?: number,
    applicabilityId?: number
  ) => {
    const updatedDate = date !== null ? date : new Date();
    dispatch({
      type: CONST.UPDATE_WAGE_FIELD_DATA,
      field: name,
      value: updatedDate,
      wageId,
      profileId,
      codeId,
      applicabilityId,
    });
  };

  const handleSelectChange = (
    selectedOption: Option | null,
    name: string,
    wageId: number,
    profileId?: number,
    codeId?: number,
    applicabilityId?: number
  ) => {
    if (name === "chequeType") {
      dispatch({
        type: CONST.UPDATE_MEAL_VOUCHERS,
        field: name,
        value: selectedOption,
        wageId,
      });
    } else {
      dispatch({
        type: CONST.UPDATE_WAGE_FIELD_DATA,
        field: name,
        value: selectedOption,
        wageId,
        profileId,
        codeId,
        applicabilityId,
      });
    }
    if (name === "autoType" && selectedOption?.value !== 1) {
      dispatch({
        type: CONST.UPDATE_WAGE_FIELD_DATA,
        field: "number",
        value: 1,
        wageId,
        profileId,
        codeId,
        applicabilityId,
      });
    }

    const validationFields = ["chequeTypeCp"];
    if (validationFields.includes(name)) {
      validation(name, selectedOption, wageId, profileId);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    scrollToTop();
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="form-border wage-premium-wrapper">
        <PCAccordion
          WageAndPremiums={wagePremiums}
          addWageData={addWageData}
          removeWageData={removeWageData}
          handleChange={handleChange}
          handleDateChange={handleDateChange}
          handleSelectChange={handleSelectChange}
          clonePayProfile={clonePayProfile}
          commProfileList={state.commProfileList}
          perfCodeList={state.perfCodeList}
          autoTypeList={state.autoTypeList}
          ecoBenefitList={state.ecoBenefitList}
        />
        <div className="hideLabel">
          <SelectWithSearch
            search={true}
            options={getPcDropdown()}
            value={""}
            onChange={(e) => addWageData(e, "pc")}
            name="pc"
            placeHolder={t("Select pc")}
          />
        </div>
      </div>
      <Navigation />
    </form>
  );
};

export default WagePemiums;
